import { useCallback } from "react";
import type { FieldValues } from "react-hook-form";
import { uploadDocument } from "@shared-kernel/application/use-cases/document-upload/upload-document";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { useApiAction } from "@shared-kernel/primary/shared/api-errors/use-api-action";
import { useFieldUpload } from "@shared-kernel/primary/shared/api-errors/use-field-upload";
import type { ActionForm } from "@shared-kernel/primary/shared/api-errors/action-form";
import type { ResumeField } from "./teacher-pedagogy.subform";
import type { TeacherPedagogyFormInputs } from "./teacher-pedagogy.subform.schema";

const UPLOAD_FAILED = "Échec du téléchargement. Le PDF est invalide ou trop volumineux.";

// Téléversements de TeacherPedagogySubform (CV, documents de méthodologie), pour les hooks d'action des formulaires qui
// l'embarquent (onboarding, onglet Ma pédagogie). Sans feedback : le refus du fichier s'affiche sous le champ
export const useTeacherPedagogyUploads = <T extends TeacherPedagogyFormInputs>(form: ActionForm<T>) => {
  const run = useApiAction();
  // Le document d'une méthodologie est nullable : useFieldUpload ne type que les champs string, d'où le formulaire non typé
  const upload = useFieldUpload(form as unknown as ActionForm<FieldValues>);

  const uploadResume = useCallback(
    (field: ResumeField, file: File) =>
      upload(field, run(uploadDocument({ document: file, asAdmin: false, role: ROLES.TEACHER })), UPLOAD_FAILED),
    [upload, run],
  );

  const uploadMethodologyDocument = useCallback(
    (rowIndex: number, file: File) =>
      upload(
        `teachingMethodologies.${rowIndex}.documentUrl`,
        run(uploadDocument({ document: file, asAdmin: false, role: ROLES.TEACHER })),
        UPLOAD_FAILED,
      ),
    [upload, run],
  );

  return { uploadResume, uploadMethodologyDocument };
};
